import React from 'react';
import { NavIcon, NavIconName } from '../../NavIcon';
import classNames from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { notificationClientName } from '@/utils/clientName';
import { ConversationAvatar, type ConversationAvatarData } from './ConversationAvatar';

dayjs.extend(relativeTime);
dayjs.locale('es');

// ---------------------------------------------------------------------------
// Shared notification row
// ---------------------------------------------------------------------------

export type NotificationRowType =
  | 'booking'
  | 'reschedule'
  | 'cancellation'
  | 'refund'
  | 'booking_confirmed'
  | 'booking_rescheduled'
  | 'booking_cancelled'
  | 'booking_refunded'
  | 'ig_screenshots_ready'
  | 'wrapped'
  | 'daily_brief'
  | 'review_received'
  | 'review_private_note'
  | 'google_review_received'
  | 'review_for_professional'
  | 'product_low_stock'
  | 'gift_card_purchased'
  | 'community_question'
  | 'community_report'
  | 'community_update_posted'
  | 'community_member_joined'
  | 'attendance_missing'
  | 'attendance_pin_locked'
  | 'premio_won'
  | 'premio_used'
  | 'premio_tomorrow'
  | 'mp_professional_disconnected'
  | 'mp_own_account_disconnected'
  | 'conversation_message'
  | 'conversation_channel'
  | 'whatsapp_template_status';

export interface NotificationTypeConfig {
  icon: NavIconName;
  action: string;
  /** Color del glyph: va suelto, sin círculo ni baldosa. */
  iconFg: string;
}

export const getNotificationTypeConfig = (type?: NotificationRowType): NotificationTypeConfig => {
  switch (type) {
    case 'reschedule':
    case 'booking_rescheduled':
      return {
        icon: 'clock',
        action: 'reagendó',
        iconFg: 'text-frost-brand'
      };
    case 'cancellation':
    case 'booking_cancelled':
      return {
        icon: 'xmark',
        action: 'canceló',
        iconFg: 'text-frost-coral'
      };
    case 'refund':
    case 'booking_refunded':
      return {
        icon: 'rotate-left',
        action: 'solicitó reembolso de',
        iconFg: 'text-frost-warning-deep'
      };
    case 'ig_screenshots_ready':
      return {
        icon: 'instagram',
        action: 'están listas',
        iconFg: 'text-[var(--color-brand-instagram)]'
      };
    case 'wrapped':
      return {
        icon: 'instagram',
        action: 'está listo',
        iconFg: 'text-[var(--color-brand-instagram)]'
      };
    case 'daily_brief':
      return {
        icon: 'calendar-days',
        action: 'está listo',
        iconFg: 'text-frost-brand'
      };
    case 'review_received':
      return {
        icon: 'star-half',
        action: 'dejó una reseña',
        iconFg: 'text-frost-warning-deep'
      };
    case 'review_for_professional':
      return {
        icon: 'star-half',
        action: 'te dejó una reseña',
        iconFg: 'text-frost-warning-deep'
      };
    case 'google_review_received':
      return {
        icon: 'google',
        action: 'dejó una reseña en Google',
        iconFg: 'text-frost-brand'
      };
    case 'review_private_note':
      return {
        icon: 'lock',
        action: 'te dejó una nota privada',
        iconFg: 'text-frost-warning-deep'
      };
    case 'product_low_stock':
      return {
        icon: 'box-open',
        action: 'tiene stock bajo',
        iconFg: 'text-frost-warning-deep'
      };
    case 'gift_card_purchased':
      return {
        icon: 'gift',
        action: 'compró una tarjeta de regalo',
        iconFg: 'text-frost-success-text'
      };
    case 'premio_won':
    case 'premio_used':
    case 'premio_tomorrow':
      return {
        icon: 'gift',
        action: 'tiene un premio',
        iconFg: 'text-frost-success-text'
      };
    case 'mp_professional_disconnected':
    case 'mp_own_account_disconnected':
      return {
        icon: 'wallet',
        action: 'desconectó su cuenta',
        iconFg: 'text-frost-warning-deep'
      };
    case 'conversation_message':
      return {
        icon: 'message',
        action: 'te escribió',
        iconFg: 'text-frost-brand'
      };
    case 'conversation_channel':
      return {
        icon: 'triangle-exclamation',
        action: 'necesita atención',
        iconFg: 'text-frost-warning-deep'
      };
    case 'whatsapp_template_status':
      return {
        icon: 'check',
        action: 'aprobó tu plantilla',
        iconFg: 'text-frost-success-text'
      };
    case 'attendance_missing':
      return {
        icon: 'clock',
        action: 'todavía no llegó',
        iconFg: 'text-frost-coral'
      };
    case 'attendance_pin_locked':
      return {
        icon: 'lock',
        action: 'bloqueó «Marcar»',
        iconFg: 'text-frost-warning-deep'
      };
    case 'community_question':
    case 'community_report':
    case 'community_update_posted':
    case 'community_member_joined':
      return {
        icon: 'comments',
        action: 'en tu comunidad',
        iconFg: 'text-frost-brand'
      };
    default:
      return {
        icon: 'check',
        action: 'reservó',
        iconFg: 'text-frost-success-text'
      };
  }
};

const AMOUNT_FORMAT = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 });

/**
 * Monto con separador de miles (es-AR: `17.500`). Formatea números y textos de número
 * crudo (`"17500"`, `"17500.5"`); un texto que ya viene con formato (`"4.500"`) o que no
 * es un número queda como vino.
 */
export const formatNotificationAmount = (amount: number | string): string => {
  if (typeof amount === 'number')
    return Number.isFinite(amount) ? AMOUNT_FORMAT.format(amount) : String(amount);
  const raw = amount.trim();
  return /^\d+(\.\d{1,2})?$/.test(raw) ? AMOUNT_FORMAT.format(Number(raw)) : amount;
};

export interface NotificationRowContent {
  customerName?: string;
  serviceName?: string;
  eventDate?: string;
  eventTime?: string;
  amount?: number | string;
  createdAt: Date | string;
  oldEventDate?: string;
  oldEventTime?: string;
  newEventDate?: string;
  newEventTime?: string;
  /** For system notifications (no customer/service): primary headline + supporting line */
  title?: string;
  subtitle?: string;
  /** Línea de advertencia bajo la fecha y el monto (p. ej. teléfono de un cliente restringido). */
  warning?: string;
  /** Mensaje de Conversaciones: la foto del cliente en lugar del ícono. */
  avatar?: ConversationAvatarData;
}

interface NotificationRowProps {
  type?: NotificationRowType;
  content: NotificationRowContent;
  unread?: boolean;
  isLast?: boolean;
  /** Optional archive button handler (API notifications only) */
  onArchive?: () => void;
  onClick?: () => void;
  /** Staggered fade-in index for the initial open. Pass `undefined` to skip */
  stagger?: number;
  variant?: 'dropdown' | 'sheet';
}

export const NotificationRow: React.FC<NotificationRowProps> = ({
  type,
  content,
  unread,
  isLast,
  onArchive,
  onClick,
  stagger,
  variant = 'dropdown'
}) => {
  const cfg = getNotificationTypeConfig(type);
  const isReschedule = type === 'reschedule' || type === 'booking_rescheduled';
  // Las reseñas también van por título + subtítulo: el título ya trae al cliente y
  // las estrellas, y el subtítulo es el extracto de lo que escribió.
  // Todo lo que trae título propio (Comunidad, stock bajo…) va por título + subtítulo: sin
  // esto caía en el renglón de reserva y se leía « reservó» sin nombre ni servicio.
  const isSystem =
    Boolean(content.title) ||
    type === 'ig_screenshots_ready' ||
    type === 'daily_brief' ||
    type === 'review_received' ||
    type === 'review_private_note' ||
    type === 'google_review_received';
  const isSheet = variant === 'sheet';

  return (
    <li
      className={classNames(
        'group/row relative cursor-pointer transition-colors duration-200 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
        'hover:bg-[rgba(var(--f-ink-rgb),0.03)]',
        isLast ? '' : 'border-b border-frost-hair'
      )}
      style={
        stagger !== undefined
          ? { animation: `notif-row-in 320ms ease-out ${stagger * 40}ms both` }
          : undefined
      }
      onClick={onClick}
    >
      {/* No leída: un punto azul quieto al costado (sin franja de color ni pulso). */}
      {unread && (
        <span
          className="absolute top-[21px] left-[5px] h-1.5 w-1.5 rounded-full bg-frost-brand"
          aria-hidden
        />
      )}

      <div className="flex items-start gap-3 py-3.5 pr-3 pl-3.5">
        {/* Ícono: el glyph de color suelto, sin círculo. Un mensaje lleva la foto del cliente. */}
        {content.avatar ? (
          <ConversationAvatar data={content.avatar} />
        ) : (
          <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center" aria-hidden>
            <NavIcon name={cfg.icon} className={classNames('text-fs-15', cfg.iconFg)} />
          </span>
        )}

        {/* Body */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {isSystem ? (
            <>
              <p className="text-fs-13 font-semibold leading-[1.35] text-frost-ink tracking-[-0.005em]">
                {content.title || 'Tus imágenes para Instagram están listas'}
              </p>
              {content.subtitle && (
                <p className="text-fs-12-5 leading-[1.35] text-frost-ink-soft">{content.subtitle}</p>
              )}
            </>
          ) : (
            <>
              {/* Customer + verb */}
              <p className="text-fs-13 leading-[1.35] text-frost-ink">
                <span className="font-semibold tracking-[-0.005em]">
                  {notificationClientName(content.customerName)}
                </span>{' '}
                <span className="text-frost-ink-mid">{cfg.action}</span>
              </p>

              {/* Service: completo, envuelve si no entra */}
              <p className="break-words text-fs-12-5 leading-[1.35] text-frost-ink-soft">
                {content.serviceName}
              </p>
            </>
          )}

          {/* Event datetime / reschedule diff */}
          {!isSystem && isReschedule && (content.oldEventDate || content.newEventDate) ? (
            <div className="mt-1 flex flex-col gap-0.5">
              <span className="inline-flex flex-wrap items-center gap-1.5 text-fs-12 tabular-nums text-frost-ink-mid">
                <span className="text-frost-ink-whisper">de</span>
                <span className="text-frost-ink-soft">
                  {content.oldEventDate} · {content.oldEventTime}
                </span>
              </span>
              <span className="inline-flex flex-wrap items-center gap-1.5 text-fs-12 tabular-nums text-frost-ink-mid">
                <span className="text-frost-brand">a</span>
                <span className="text-frost-brand">
                  {content.newEventDate} · {content.newEventTime}
                </span>
              </span>
            </div>
          ) : !isSystem && ((content.eventDate && content.eventTime) || content.amount) ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
              {content.eventDate && content.eventTime && (
                // Fecha del turno en texto liso: sin caja gris ni tipografía monoespaciada.
                <span className="inline-flex items-center gap-1.5 text-fs-12 leading-tight tabular-nums text-frost-ink-mid">
                  {/* Ancho del dibujo (0.875em), no la caja fija de 1.25em de NavIcon. */}
                  <NavIcon name="calendar-days" className="w-[0.875em] text-fs-11 text-frost-ink-whisper" />
                  <span>
                    {content.eventDate} · {content.eventTime}
                  </span>
                </span>
              )}
              {content.amount !== undefined && content.amount !== null && content.amount !== '' && (
                <span className="text-fs-12-5 leading-tight font-semibold tabular-nums text-frost-ink">
                  ${formatNotificationAmount(content.amount)}
                </span>
              )}
            </div>
          ) : null}

          {!isSystem && content.warning && (
            <p className="mt-1.5 text-fs-13 leading-[1.45] text-frost-warning-text">{content.warning}</p>
          )}

          {/* Hace cuánto: tal cual sale de dayjs, sin mayúsculas forzadas ni tracking */}
          <span className="mt-1 text-fs-11-5 leading-tight font-medium text-frost-ink-whisper">
            {dayjs(content.createdAt).fromNow()}
          </span>
        </div>

        {/* Archivar: la X suelta, sin caja */}
        {onArchive && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onArchive();
            }}
            aria-label="Archivar notificación"
            className={classNames(
              '-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-frost-ink-whisper transition-[color,opacity] duration-150 hover:text-frost-ink focus-visible:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-frost-brand/30',
              isSheet ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'
            )}
          >
            <NavIcon name="xmark" className={isSheet ? 'text-fs-13' : 'text-fs-12'} />
          </button>
        )}
      </div>
    </li>
  );
};
